import { useEffect, useRef } from "react"
import { Box, Text, Flex, Tag, Image, Link, Button, Icon, Drawer, Portal, CloseButton } from "@chakra-ui/react"
import { FaLinkedinIn, FaXTwitter, FaFacebookF } from "react-icons/fa6"
import { FiExternalLink, FiFileText, FiPlayCircle } from "react-icons/fi"
import { isBefore } from "date-fns"
import { useDisclosure } from "@chakra-ui/react"
import { useGlobalState } from "providers/globalState"
import { useProgramGuideTheme } from "providers/programGuideTheme"
import { useRoomColor } from "providers/roomColor"
import { useTrackColorGetter } from "providers/trackColor"
import { useConference, useConferenceFacultyProfile } from "services/emma/emma"
import FacultyProfile from "components/faculty/facultyProfile"
import LoadingIndicator from "components/ui/LoadingIndicator"
import FormattedDateString from "components/agenda/formattedDateString"
import FormattedTimeString from "components/agenda/formattedTimeString"
import AbstractModal, { findAbstractLink } from "components/agenda/AbstractModal"
import Highlighter from "components/agenda/highlighter"
import { useEventLink } from "components/agenda/renderLink"
import externalCME from "config/externalCME"
import { useSessionLinks } from "lib/sessionUrl"
import { isAppView } from "src/util"
import { roomsHidden } from "lib/rooms"
import { facultyPhotoUrl } from "util/agendaMedia"

function aggregateFaculty(session) {
    const groupMap = {}

    function addGroup(group) {
        const key = group.role?.slug || group.role?.name || "unknown"
        if (!groupMap[key]) groupMap[key] = { role: group.role, users: [] }
        group.users?.forEach(user => {
            if (!groupMap[key].users.find(u => u.id === user.id)) {
                groupMap[key].users.push(user)
            }
        })
    }

    session.moderators?.forEach(addGroup)
    session.children?.forEach(child => child.moderators?.forEach(addGroup))

    return Object.values(groupMap).filter(g => g.users.length > 0)
}

// A presentation's faculty groups as they should be listed: nothing unless the meeting shows
// moderators, and roles that share a display name (e.g. two roles both shown as "Panelists")
// merged into one group, de-duplicating users. Emma normally sends one group per role, so the
// merge is a safeguard carried over from the old agenda (components/agenda/presentation.js).
export function useFacultyGroups(groups) {
    const { site } = useGlobalState()
    if (!site?.meeting?.showModerators) return []

    const merged = []
    ;(groups || []).forEach(group => {
        if (!group.users?.length) return
        const label = group.role?.display_name || group.role?.name
        const existing = merged.find(g => (g.role?.display_name || g.role?.name) === label)
        if (!existing) {
            merged.push({ ...group, users: [...group.users] })
            return
        }
        group.users.forEach(user => {
            if (!existing.users.some(u => u.id === user.id)) existing.users.push(user)
        })
    })
    return merged
}

// Whether faculty names open the profile drawer, per the meeting's settings.
function useFacultyLinksEnabled() {
    const { site } = useGlobalState()
    return Boolean(site?.meeting?.enableFacultyProfileLinks) && !site?.suppressModeratorLinks
}

// Presenters whose drawer omits the "full faculty profile page" link — they have no full profile.
const PRESENTER_ROLES_WITHOUT_PROFILE = ["abstract-presenter", "challenging-case-presenter", "satellite-presenter"]

// The accent color for a session panel: an explicit accentColor from the parent wins; otherwise
// the session's room highlight color, then the theme default. Shared with PresentationDetail so
// the presentation drawer is tinted exactly like the session it belongs to.
export function useSessionAccent(session, accentColor) {
    const { sessionDetail: sd } = useProgramGuideTheme()
    const roomColor = useRoomColor(session?.venue)
    return accentColor ?? roomColor ?? sd.accentColor
}

// `actionsInsetRight` reserves room at the end of the top row for an affordance that overlays the
// panel — the mobile drawer's close button is absolutely positioned in that same corner and would
// otherwise sit on top of the share buttons. Any Chakra padding value; 0 in the desktop column.
//
// `forStreaming` drops the room, accreditation and Print button (the live/streaming pages show those
// elsewhere). `channelSlug` names the site channel the session is shown under: it supplies the
// channel status line and the live/on-demand links on presentation titles. `type` is the session's
// format ("ondemand" hides date, time and room, and prints the on-demand version); it defaults to
// `session.type`. `suppressLinks` turns the live/on-demand title links off.
//
// `onSelectPresentation(presentation)` turns each row of the Presentations list into a link to
// that presentation's own deep link (…/<sessionId>/<presentationId>); without it the rows are
// plain text, as in the print view. `highlightPresentationId` names the row to draw the transient
// "you were here" card behind — the presentation whose drawer just closed.
export default function SessionDetail({
    session,
    accentColor,
    actionsInsetRight = 0,
    onSelectPresentation,
    highlightPresentationId = null,
    forStreaming = false,
    channelSlug = null,
    type = null,
    suppressLinks = false,
}) {
    const { site } = useGlobalState()
    const { sessionDetail: sd } = useProgramGuideTheme()
    const sessionLinks = useSessionLinks()
    const accent = useSessionAccent(session, accentColor)

    if (!session) {
        return (
            <Box p={6} color="gray.500" fontSize="18px">
                Select an event to view its details…
            </Box>
        )
    }

    const showAddEvent =
        session.depth < 1 &&
        (session.addevent_id || session.addevent_dynamic) &&
        isBefore(new Date(), new Date(session.datetime_end))

    // Aggregate faculty from the session AND its children (top-level sessions often have empty moderators[])
    // Sessions laid out as two or more section heads list their own faculty per section, so the
    // aggregated panel and the "Presentations" label above the list are left out. A lone section
    // (e.g. a wrapper beside a "Check-In") still gets the aggregated panel.
    const presentationRows = getPresentations(session)
    const hasSections = presentationRows.filter(row => row.isSection && !row.titleOnly).length >= 2
    const allFacultyGroups = hasSections || !site?.meeting?.showModerators ? [] : aggregateFaculty(session)
    // Every moderator role (Moderator, Co-Moderator, Live Case Moderator…) shares one row, plain
    // "moderator" first; the other roles each get a row of their own below.
    const moderatorGroups = allFacultyGroups
        .filter(g => g.role?.slug?.includes("moderator") || g.role?.name?.toLowerCase().includes("moderator"))
        .sort((a, b) => (b.role?.slug === "moderator") - (a.role?.slug === "moderator"))
    const otherFacultyGroups = allFacultyGroups.filter(g => !moderatorGroups.includes(g))

    return (
        <Box h="100%" {...sd.container}>
            <SessionHeader
                session={session}
                accent={accent}
                actionsInsetRight={actionsInsetRight}
                forStreaming={forStreaming}
                channelSlug={channelSlug}
                type={type}
            />
            {moderatorGroups.length > 0 && (
                <Flex columnGap={8} flexWrap="wrap">
                    {moderatorGroups.map(group => (
                        <FacultyAvatarGroup key={group.role?.id} group={group} accent={accent} />
                    ))}
                </Flex>
            )}
            {otherFacultyGroups.map(group => (
                <FacultyAvatarGroup key={group.role?.id} group={group} accent={accent} />
            ))}
            {/* Presentations */}
            {session.children?.length > 0 && (
                // Three columns shared by every row (each row is a subgrid): top-level times, nested
                // times, titles. Top-level titles start at the second column, so nested rows (heads
                // inside heads, see getPresentations) start right on that line — their times in the second column, their titles in
                // the third. Each time column is as wide as its widest time, so the lines hold
                // whatever the times' widths. The gap is padding on the time cells rather than a
                // column gap, so a session with no nested rows gets no extra space.
                <Box mb={6} display="grid" gridTemplateColumns="max-content max-content 1fr">
                    {/* "Presentations" label suppressed for now; restore by uncommenting.
                    {!hasSections && (
                        <Text color={accent} {...sd.sectionLabel}>
                            Presentations
                        </Text>
                    )}
                    */}
                    {presentationRows.map(({ node: child, isSection, titleOnly, nested, endsSection }) => (
                        <PresentationItem
                            key={child.id}
                            isSection={isSection}
                            nested={nested}
                            endsSection={endsSection}
                            titleOnly={titleOnly}
                            accent={accent}
                            presentation={child}
                            href={onSelectPresentation ? sessionLinks.path(session.id, child.id) : null}
                            onSelect={onSelectPresentation}
                            isHighlighted={child.id === highlightPresentationId}
                            channelSlug={channelSlug}
                            suppressLinks={suppressLinks}
                            // A section head's own faculty is already in the aggregated panel
                            // unless that panel is left out for a multi-section session.
                            showSectionFaculty={hasSections}
                        />
                    ))}
                </Box>
            )}
            <Flex mt={4} gap={2} flexWrap="wrap">
                {showAddEvent && (
                    <Link
                        href={session.addevent_dynamic || `https://www.addevent.com/event/${session.addevent_id}`}
                        target="_blank"
                        _hover={{ textDecoration: "none" }}
                    >
                        <Button size="sm" variant="outline">
                            + Add to calendar
                        </Button>
                    </Link>
                )}
                {!forStreaming && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="program__printBtn no-print"
                        onClick={() =>
                            window.open(
                                `/program-guide/print/session/${session.id}` +
                                    ((type ?? session.type) === "ondemand" ? "?type=ondemand" : ""),
                            )
                        }
                    >
                        Print
                    </Button>
                )}
            </Flex>
        </Box>
    )
}

// The session facts that head both the session panel and the presentation drawer: type pill and
// share buttons on the top row, then title, track/topic tags, description, accreditation, date/time
// and room. `presentation`, when given, makes the share buttons publish that presentation's link
// instead of the session's. `compact` drops the session prose (description, accreditation) — in the
// presentation drawer the session is context, not the subject. `forStreaming`, `channelSlug` and
// `type` are as on SessionDetail.
//
// Carried over from the old agenda (components/agenda/session.js): date, time and room are left out
// for virtual meetings and on-demand sessions; tracks show only when the meeting groups events at
// the second level and doesn't hide tracks; the description shows only for the session types in
// DESCRIPTION_SESSION_TYPES.
const DESCRIPTION_SESSION_TYPES = ["Exhibit Hall", "Academic Satellite Program"]

export function SessionHeader({
    session,
    accent,
    actionsInsetRight = 0,
    presentation = null,
    compact = false,
    forStreaming = false,
    channelSlug = null,
    type = null,
}) {
    const { site } = useGlobalState()
    const { sessionDetail: sd } = useProgramGuideTheme()
    const getTrackColor = useTrackColorGetter()
    const venueLabel = useVenueLabel(session.venue)

    const isOnDemand = (type ?? session.type) === "ondemand" || Boolean(session.params?.on_demand)
    const showSchedule = site?.meeting?.type !== "Virtual" && !isOnDemand
    const channel = channelSlug ? site?.channels?.find(c => c.slug === channelSlug) : null
    const showChannelStatus = Boolean(channel?.channelStatus) && channel.channelStatus !== "default"
    const showTracks = !site?.meeting?.hideTracks && Boolean(site?.meeting?.groupEventAtSecondLevel)
    const tracks = showTracks ? session.tracks || [] : []
    const showDescription =
        !compact && DESCRIPTION_SESSION_TYPES.includes(session.session_type) && Boolean(session.description)

    const accreditationText = externalCME[site?.meeting?.siteId]?.includes(session.root_id)
        ? "This content is CME accredited."
        : session.params?.accreditation_text

    return (
        <>
            {/* Session type and share buttons share the top row. `ml="auto"` rather than
                space-between so the buttons stay right-aligned even on sessions with no type pill,
                and the row wraps rather than squeezing them when the type name runs long. */}
            <Flex align="flex-start" gap={3} mb={3} flexWrap="wrap" pr={actionsInsetRight}>
                {session.session_type && (
                    <Tag.Root size="md" variant="solid" bg={accent} borderRadius="full" px={3} py={1} minW={0}>
                        <Tag.Label fontSize="13px" color="white">
                            {session.session_type}
                        </Tag.Label>
                    </Tag.Root>
                )}
                <Flex gap={1} ml="auto" flexShrink={0}>
                    <ShareButtons session={session} presentation={presentation} />
                </Flex>
            </Flex>
            <Text lineHeight="1.3" color={accent} mb={1} {...sd.title}>
                <Highlighter hit={!!session.searchValue} text={session.title} />
            </Text>
            {(tracks.length > 0 || session.event_topics?.length > 0) && (
                <Box mt={2} mb={3}>
                    {tracks.length > 0 && (
                        <Flex gap={1} flexWrap="wrap">
                            {tracks.map((track, i) => (
                                <Tag.Root size="sm" key={i} variant="solid" {...sd.trackTag} bg={getTrackColor(track)}>
                                    <Tag.Label>{track}</Tag.Label>
                                </Tag.Root>
                            ))}
                        </Flex>
                    )}
                    {/* Topics render on their own line, below the tracks */}
                    {session.event_topics?.length > 0 && (
                        <Flex gap={1} flexWrap="wrap" mt={tracks.length > 0 ? 1 : 0}>
                            {session.event_topics.map((topic, i) => (
                                <Tag.Root size="sm" key={i} variant="solid" {...sd.topicTag}>
                                    <Tag.Label>{topic}</Tag.Label>
                                </Tag.Root>
                            ))}
                        </Flex>
                    )}
                </Box>
            )}
            {showDescription && (
                <Text className="program__sessionDescription" fontSize={15} lineHeight={1.2} mt={1} mb={3} {...sd.meta}>
                    <Highlighter hit={!!session.searchValue} text={session.description} />
                </Text>
            )}
            {!compact && !forStreaming && accreditationText && (
                <Text className="program__cmeText" mb={2} mt={0} {...sd.meta}>
                    {accreditationText}
                </Text>
            )}
            <Box mb={4}>
                {showSchedule && (
                    <>
                        <Text className="program__sessionDate" mb={0} mt={0} {...sd.meta}>
                            <FormattedDateString date={session.datetime_start} format="EEEE, MMMM d" />
                            {" · "}
                            <FormattedTimeString event={session} config={{ showEndTime: true, showTimezone: true }} />
                            {/*{session.duration && ` · ${session.duration}m`}*/}
                        </Text>
                        {!forStreaming && !roomsHidden(site) && (
                            <Text className="program__sessionRoom" mb={0} mt={0} {...sd.meta}>
                                {venueLabel || "Room TBD"}
                            </Text>
                        )}
                    </>
                )}
                {showChannelStatus && (
                    <Text mb={0} mt={0} fontWeight={600} {...sd.meta}>
                        {channel.channelStatus}
                    </Text>
                )}
            </Box>
        </>
    )
}

// "Station N" rooms are numbered spots inside a larger space, so the bare name doesn't say where to
// go — they always show the full breadcrumb, wherever they are.
export const isStationRoom = name => /^Station\s+\d+$/i.test(name?.trim() ?? "")

// The label for a session's room. Rooms inside the conference's main venue are named on their own —
// the building is a given — while rooms anywhere else (an off-site hotel, say) keep the full
// breadcrumb so readers know to go elsewhere. Event breadcrumbs run room-first ("Room | Venue"), so
// membership is read off how the breadcrumb ends, since the slimmed program feed carries no
// parent_id. Until the conference loads, the breadcrumb is the safe default. Shared with
// SessionCard so the list and the detail panel name rooms the same way. Null when the meeting hides
// rooms.
export function useVenueLabel(venue) {
    const { site } = useGlobalState()
    const meetingId = site?.meeting?.emmaMeetingId
    const { data: conference } = useConference(meetingId, null, !!meetingId)
    if (!venue || roomsHidden(site)) return null
    if (isStationRoom(venue.name)) return venue.breadcrumb || venue.name
    const main = conference?.venue?.name
    const inMainVenue = Boolean(main && venue.breadcrumb?.trim().endsWith(main))
    return inMainVenue ? venue.name : venue.breadcrumb || venue.name
}

// Brand presentation for each network: filled button in the network's own colour with a white
// glyph. Kept as data so the three buttons cannot drift apart in size, spacing or behaviour.
//
// LinkedIn's share-offsite endpoint and Facebook's sharer both accept only a URL — the preview each
// renders comes entirely from the Open Graph tags on the shared page, not from anything passed
// here. (LinkedIn's older shareArticle endpoint took title/summary params but has been superseded,
// and it ignored them in favour of og: tags anyway.) Only X takes custom text.
const SHARE_TARGETS = [
    {
        network: "LinkedIn",
        icon: FaLinkedinIn,
        bg: "#0a66c2",
        hoverBg: "#004182",
        href: shareUrl => "https://www.linkedin.com/sharing/share-offsite/?url=" + encodeURIComponent(shareUrl),
    },
    {
        network: "X",
        icon: FaXTwitter,
        bg: "#000000",
        hoverBg: "#272727",
        href: (shareUrl, text) =>
            "https://x.com/intent/post?url=" + encodeURIComponent(shareUrl) + "&text=" + encodeURIComponent(text),
    },
    {
        network: "Facebook",
        icon: FaFacebookF,
        bg: "#1877f2",
        hoverBg: "#0c63d4",
        href: shareUrl => "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(shareUrl),
    },
]

// LinkedIn, X and Facebook share buttons for a single session, or for one presentation within it
// when `presentation` is given. The URL is built from the session in props rather than from
// window.location so it names the session the reader is actually looking at even if the address bar
// has drifted, and it deliberately carries no filter or search params — see lib/sessionUrl. Opened
// in a sized popup, the convention these share dialogs expect.
export function ShareButtons({ session, presentation = null }) {
    const sessionLinks = useSessionLinks()
    const { site } = useGlobalState()

    // Share text names only the thing being shared plus the meeting: the presentation title alone
    // for a presentation link, the session title for a session link. The session a presentation
    // belongs to is on the shared page itself.
    const subject = presentation ? presentation.title : session.title
    const shareText = site.meeting?.meetingName ? `${subject} — ${site.meeting.meetingName}` : subject
    const shareLabel = subject

    function openShareWindow(buildHref) {
        const shareUrl = sessionLinks.shareUrl(session.id, presentation?.id ?? null)
        if (!shareUrl) return
        window.open(buildHref(shareUrl, shareText), "_blank", "noopener,noreferrer,width=600,height=500")
    }

    return (
        <>
            {SHARE_TARGETS.map(target => (
                <Button
                    key={target.network}
                    size="xs"
                    className="no-print"
                    aria-label={`Share "${shareLabel}" on ${target.network}`}
                    title={`Share on ${target.network}`}
                    bg={target.bg}
                    color="white"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor={target.bg}
                    _hover={{ bg: target.hoverBg, borderColor: target.hoverBg }}
                    // Conference CSS sets a locked-in fill on svgs, so the glyph colour is forced
                    // on the element itself rather than left to inherit — same treatment as the
                    // accordion chevron in ./SessionList.
                    css={{ "& svg": { color: "white", fill: "currentColor" } }}
                    onClick={() => openShareWindow(target.href)}
                >
                    <Icon as={target.icon} />
                </Button>
            ))}
        </>
    )
}

function FacultyDrawerLink({ user, children }) {
    const { site } = useGlobalState()
    const { open, onOpen, onClose } = useDisclosure()
    const linksEnabled = useFacultyLinksEnabled()

    if (!linksEnabled) return <Box as="span">{children}</Box>

    const showProfileLink =
        !isAppView() && !site.isWebView && !PRESENTER_ROLES_WITHOUT_PROFILE.includes(user.meeting_role)

    return (
        <>
            <Box as="span" onClick={onOpen} cursor="pointer">
                {children}
            </Box>
            <Drawer.Root open={open} onOpenChange={({ open }) => !open && onClose()} placement="end" size="lg">
                <Portal>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Drawer.Content>
                            {showProfileLink && (
                                <Link
                                    href={`/faculty/${user.first_name}-${user.last_name}-${user.id}`}
                                    display="block"
                                    color="#333"
                                    textDecoration="underline"
                                    m="20px 0 0 25px"
                                    target="_blank"
                                >
                                    Go to full faculty profile page
                                </Link>
                            )}
                            <Drawer.Body color="#333">
                                <DrawerFacultyProfile facultyId={user.id} emmaMeetingId={site.meeting.emmaMeetingId} />
                            </Drawer.Body>
                            <Drawer.CloseTrigger asChild>
                                <CloseButton size="sm" />
                            </Drawer.CloseTrigger>
                        </Drawer.Content>
                    </Drawer.Positioner>
                </Portal>
            </Drawer.Root>
        </>
    )
}

function DrawerFacultyProfile({ emmaMeetingId, facultyId }) {
    const { data, status } = useConferenceFacultyProfile(emmaMeetingId, facultyId)
    return status === "success" ? (
        <FacultyProfile profile={data} context="drawer" />
    ) : (
        <LoadingIndicator color="color1" />
    )
}

// A role's label for `count` people. The aggregated panel merges a role's users across the session's
// children, so Emma's per-occurrence display_name (singular or plural for that occurrence's own count)
// can be wrong for the merged group; name / name_plural are picked from the merged count instead.
function roleLabel(role, count) {
    const label = count === 1 ? role?.name : role?.name_plural
    return label || role?.display_name || role?.name
}

// One aggregated faculty role: its label, then each person's photo and name.
function FacultyAvatarGroup({ group, accent }) {
    const { sessionDetail: sd } = useProgramGuideTheme()
    return (
        <Box mb={6}>
            <Text color={accent} mb={2} {...sd.sectionLabel}>
                {roleLabel(group.role, group.users.length)}
            </Text>
            <Flex gap={2} flexWrap="wrap">
                {group.users.map(user => (
                    <FacultyAvatar key={user.id} user={user} />
                ))}
            </Flex>
        </Box>
    )
}

export function FacultyAvatar({ user }) {
    const imgUrl = facultyPhotoUrl(user.okta_id)

    return (
        <FacultyDrawerLink user={user}>
            <Flex direction="column" align="center" gap={1} maxW="80px">
                <Box w="60px" h="60px" borderRadius="full" overflow="hidden" bg="gray.200" flexShrink={0}>
                    <Image
                        src={imgUrl}
                        alt={user.full_name}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                        onError={e => {
                            e.currentTarget.src = "/person.png"
                        }}
                    />
                </Box>
                <Text fontSize="12px" textAlign="center" color="gray.700" lineHeight="1.2">
                    <Highlighter hit={true} text={user.full_name} />
                    {user.degree ? ` ${user.degree}` : ""}
                </Text>
            </Flex>
        </FacultyDrawerLink>
    )
}

export function FacultyPill({ user }) {
    const linksEnabled = useFacultyLinksEnabled()

    return (
        <FacultyDrawerLink user={user}>
            <Tag.Root
                size="sm"
                variant="outline"
                colorPalette="gray"
                cursor={linksEnabled ? "pointer" : undefined}
                _hover={linksEnabled ? { bg: "gray.100" } : undefined}
                px={2}
                py={1.5}
            >
                <Tag.Label fontSize="14px">
                    <Highlighter hit={true} text={user.full_name} />
                    {user.degree ? `, ${user.degree}` : ""}
                </Tag.Label>
            </Tag.Root>
        </FacultyDrawerLink>
    )
}

// The session's presentations as a flat list of rows, following the old agenda's layout
// (components/agenda/presentation.js). A lone wrapper child just repeats the session, so it is
// skipped and its talks sit at the top level. When the session has several children, each one that
// has children of its own is a section head (`isSection`) followed by its children as ordinary
// rows; childless siblings such as "Check-In" are ordinary rows too. Events at depth 2 or deeper
// with children are title-only heads. Rows are `nested` (they start on the top-level title line,
// see the grid in SessionDetail) only where heads double up: a head inside another head, and
// everything under it. A single level of heads stays flush, so the indent marks the exception.
function getPresentations(session) {
    const items = session.children || []
    const isWrapped = items.length === 1 && items[0].children?.length > 0
    const top = isWrapped ? items[0].children : items
    const rows = []
    // `heads` counts the section heads enclosing these nodes. An event at depth 2 or deeper with
    // children is a title-only section head, followed by its children.
    const walk = (nodes, heads) =>
        nodes.forEach(node => {
            if (node.depth >= 2 && node.children?.length) {
                rows.push({ node, nested: heads >= 1, isSection: true, titleOnly: true })
                walk(node.children, heads + 1)
                rows[rows.length - 1].endsSection = true
                return
            }
            rows.push({ node, nested: heads >= 2, isSection: false })
            if (node.children?.length) walk(node.children, heads)
        })
    top.forEach(node => {
        if (!isWrapped && top.length > 1 && node.children?.length) {
            rows.push({ node, nested: false, isSection: true })
            // A top-level head only groups the session's talks (often just the wrapper beside a
            // "Check-In"), so it doesn't count toward nesting.
            walk(node.children, 0)
            rows[rows.length - 1].endsSection = true
        } else {
            walk([node], 0)
        }
    })
    // A section's last row gets space below it, so talks that follow at the head's own level don't
    // read as part of the section. Not needed before another head (heads have their own top
    // padding) or at the end of the list.
    rows.forEach((row, i) => {
        if (row.endsSection && (i === rows.length - 1 || rows[i + 1].isSection)) row.endsSection = false
    })
    return rows
}

// One row of a session's Presentations list. With `href` + `onSelect` the title is a real anchor to
// the presentation's deep link — so it can be copied, opened in a new tab, or shared — whose plain
// click is intercepted to open the presentation drawer in place rather than reload the guide.
//
// While the meeting is live, a row can also carry a live-channel / on-demand / special-case link
// (useEventLink, as in the old agenda). Without a deep link the title itself is that link; with
// one, the title keeps the deep link and the live link sits after it as an icon, since anchors
// cannot nest. `suppressLinks` and "Break" rows never get one.
//
// `isHighlighted` draws the theme's presentationHighlight card behind the row and scrolls the row
// into view. The card is a pseudo-element, not padding or a border on the row itself, so the text
// stays exactly where it was as the card fades in and out. The row isolates its stacking context
// so the card's negative z-index sits behind the row's content but above the panel background.
export function PresentationItem({
    presentation,
    href = null,
    onSelect = null,
    isHighlighted = false,
    isSection = false,
    titleOnly = false,
    nested = false,
    endsSection = false,
    accent,
    channelSlug = null,
    suppressLinks = false,
    showSectionFaculty = true,
}) {
    const { site } = useGlobalState()
    const { sessionDetail: sd } = useProgramGuideTheme()
    const rowRef = useRef(null)
    const visibleFacultyGroups = useFacultyGroups(presentation.moderators)
    // Title-only (depth 2+) heads sit below the aggregated panel's reach, so they always list theirs.
    const facultyGroups = !isSection || titleOnly || showSectionFaculty ? visibleFacultyGroups : []
    const linksEnabled = useFacultyLinksEnabled()
    const eventLink = useEventLink(presentation, channelSlug, site)

    useEffect(() => {
        if (!isHighlighted) return
        rowRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }, [isHighlighted])

    // Section heads are labels, not deep-link targets, so they never render an anchor.
    const isLink = Boolean(href && onSelect) && !isSection
    const liveHref = !suppressLinks && site?.meeting?.isLive && presentation.title !== "Break" ? eventLink.href : null
    const LiveIcon = eventLink.linkIcon === "play" ? FiPlayCircle : FiExternalLink
    const showTime = !isSection && !presentation.params?.on_demand
    const abstractLink = findAbstractLink(presentation.links)

    function handleClick(e) {
        // Leave modified clicks (new tab, new window) to the browser.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
        e.preventDefault()
        onSelect(presentation)
    }

    const titleText = <Highlighter hit={!!presentation.searchValue} text={presentation.title} />

    return (
        <Box
            ref={rowRef}
            py={2}
            pt={isSection ? 4 : undefined}
            mb={endsSection ? 8 : undefined}
            display="grid"
            // Nested rows span only the nested-time and title columns, so they start on the
            // top-level title line; within its own span every row is time, then title.
            gridColumn={nested ? "2 / -1" : "1 / -1"}
            gridTemplateColumns="subgrid"
            alignItems="baseline"
            borderBottom="1px solid"
            {...sd.presentationDivider}
            position="relative"
            css={{ isolation: "isolate" }}
            // The card hides the divider under it; the divider returns as the card fades out.
            borderBottomColor={isHighlighted ? "transparent" : undefined}
            transition={isHighlighted ? "border-color 0.3s ease" : "border-color 1s ease"}
            // The row already has 8px of vertical padding, so -2px vertically and -10px horizontally
            // puts the card's edge 10px from the text on every side.
            _before={{
                content: '""',
                position: "absolute",
                inset: "-2px -10px",
                zIndex: -1,
                pointerEvents: "none",
                ...sd.presentationHighlight,
                opacity: isHighlighted ? 1 : 0,
                transition: isHighlighted ? "opacity 0.3s ease" : "opacity 1s ease",
            }}
        >
            {/* Heads start at the row's own left edge. Other rows always render the time cell —
                empty for on-demand rows — so their title stays in the title column. */}
            {!isSection && (
                <Text whiteSpace="nowrap" mt={0} pr={3} {...sd.presentationTime}>
                    {showTime && <FormattedTimeString event={presentation} config={{ showEndTime: false }} />}
                </Text>
            )}
            <Box gridColumn={isSection ? "1 / -1" : "2 / -1"}>
                <Text
                    mt={0}
                    {...sd.presentationTitle}
                    {...(isSection ? { fontWeight: sd.sectionLabel.fontWeight, color: accent } : {})}
                >
                    {isLink ? (
                        <Link
                            href={href}
                            onClick={handleClick}
                            color="inherit"
                            textDecoration="underline"
                            textDecorationColor="gray.300"
                            textUnderlineOffset="3px"
                            _hover={{ color: "gray.800", textDecorationColor: "currentColor" }}
                            _focusVisible={{ outline: "2px solid", outlineColor: "gray.400", outlineOffset: "2px" }}
                        >
                            {titleText}
                        </Link>
                    ) : liveHref ? (
                        <Link href={liveHref} target="_blank" color="inherit" textDecoration="underline">
                            {titleText}
                            <Icon as={LiveIcon} boxSize="18px" ml={1} verticalAlign="text-bottom" />
                        </Link>
                    ) : (
                        titleText
                    )}
                    {isLink && liveHref && (
                        <Link
                            href={liveHref}
                            target="_blank"
                            color="inherit"
                            ml={1}
                            aria-label={eventLink.linkIcon === "play" ? "Watch" : "Open link"}
                        >
                            <Icon as={LiveIcon} boxSize="18px" verticalAlign="text-bottom" />
                        </Link>
                    )}
                </Text>
                {presentation.description && (
                    <Text
                        className="program__presentationDescription"
                        fontSize={15}
                        lineHeight={1.2}
                        mt={1}
                        mb={1}
                        {...sd.meta}
                    >
                        <Highlighter hit={!!presentation.searchValue} text={presentation.description} />
                    </Text>
                )}
                {facultyGroups.map(group => (
                    <Text key={group.role?.id ?? group.role?.name} fontSize="14px" color="gray.500" mt={0.5}>
                        {group.role?.display_name || group.role?.name}:{" "}
                        {group.users.map((u, i) => (
                            <Text as="span" key={u.id}>
                                <FacultyDrawerLink user={u}>
                                    <Text
                                        as="span"
                                        textDecoration={linksEnabled ? "underline" : undefined}
                                        cursor={linksEnabled ? "pointer" : undefined}
                                        _hover={linksEnabled ? { color: "gray.700" } : undefined}
                                    >
                                        <Highlighter hit={true} text={u.full_name} />
                                        {u.degree ? `, ${u.degree}` : ""}
                                    </Text>
                                </FacultyDrawerLink>
                                {i < group.users.length - 1 && ", "}
                            </Text>
                        ))}
                    </Text>
                ))}
                {abstractLink && <AbstractButton link={abstractLink} title={presentation.title} />}
            </Box>
        </Box>
    )
}

// A presentation's "View Abstract" button, which opens the abstract in a modal (AbstractModal).
function AbstractButton({ link, title }) {
    const { open, onOpen, onClose } = useDisclosure()

    return (
        <>
            <Button size="xs" variant="outline" className="no-print" mt={2} onClick={onOpen}>
                <Icon as={FiFileText} />
                {link.title || "View Abstract"}
            </Button>
            <AbstractModal open={open} onClose={onClose} url={link.url} title={title} />
        </>
    )
}
